import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import LinearProgress from "@mui/material/LinearProgress";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import { foyerApi } from "../http/foyer";
import { PageHeader } from "./PageHeader";
import { useFormat } from "../i18n/format";
import { errorText } from "../utils/apiError";
import {
  formatDuration,
  formatPercent,
  EngagementBreakdown,
  EngagementMetric,
} from "./engagementFormat";
import type { EngagementSummary } from "@shared/types/engagement";

/**
 * Content-engagement dashboard for one premise: which sections, streaming apps,
 * promotions and radio stations guests actually use on the in-room TVs - and,
 * for the ones we can time, how long they hold attention.
 *
 * Everything here is a count or a duration over a UTC-day rollup. There is no
 * per-guest or per-room breakdown by design (a hotel TV sits in a private room,
 * so finer grain would be surveillance rather than product analytics), and the
 * panel says so out loud: that stance is a reason to buy this in Europe, not a
 * gap to apologise for. Reads `GET /api/cms/v1/premise/:id/engagement`.
 */

const RANGES = [
  { value: 7, days: 7 },
  { value: 30, days: 30 },
  { value: 90, days: 90 },
  { value: 365, months: 12 },
];

export default function EngagementPanel({
  premiseId,
  showCasting = true,
}: {
  premiseId: string;
  /**
   * Whether to report screen sharing. False on a care premise: casting is a
   * hospitality function, those boxes ship with Chromecast removed, and a
   * permanent "Screen shares: 0" reads as a feature they have failed to use
   * rather than one they were never sold.
   */
  showCasting?: boolean;
}) {
  const [days, setDays] = useState(30);
  const [data, setData] = useState<EngagementSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation(["analytics", "common"]);
  const fmt = useFormat();
  const duration = (seconds: number | undefined | null) =>
    formatDuration(seconds, t, fmt);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await foyerApi.engagement(premiseId, days));
      setError(null);
    } catch (err) {
      setError(errorText(err, t));
    } finally {
      setLoading(false);
    }
  }, [premiseId, days, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const totals = data?.totals;
  // A premise whose TVs are all on an older bundle reports nothing at all -
  // say so explicitly rather than showing a wall of confident zeros.
  const hasData =
    !!totals &&
    totals.sectionViews +
      totals.appLaunches +
      totals.radioPlays +
      totals.castStarts +
      totals.slotViews >
      0;

  return (
    <Stack spacing={3}>
      <PageHeader
        eyebrow={t("engagementPanel.eyebrow")}
        title={t("engagementPanel.title")}
        help={t("engagementPanel.help")}
        actions={
          <>
            <TextField
              select
              size="small"
              label={t("engagementPanel.range")}
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              data-testid="engagement-range"
              sx={{ minWidth: 160 }}
            >
              {RANGES.map((r) => (
                <MenuItem key={r.value} value={r.value}>
                  {r.months !== undefined
                    ? t("common:labels.lastMonths", { count: r.months })
                    : t("common:labels.lastDays", { count: r.days })}
                </MenuItem>
              ))}
            </TextField>
            {/* A plain link, not a fetch: the browser streams the file and
                carries the session cookie itself, so a year of data never has
                to be materialised in a JS string first. */}
            <Button
              component="a"
              href={foyerApi.engagementCsvUrl(premiseId, days)}
              startIcon={<DownloadOutlinedIcon />}
              variant="outlined"
              size="medium"
              data-testid="engagement-export"
            >
              {t("engagementPanel.exportCsv")}
            </Button>
          </>
        }
      />

      {error && <Alert severity="error">{error}</Alert>}
      {loading && !data && <CircularProgress size={24} />}

      {data && (
        <>
          <Card variant="outlined">
            <CardContent>
              <Stack direction="row" spacing={5} flexWrap="wrap" useFlexGap>
                <EngagementMetric
                  label={t("engagementPanel.metrics.sectionViews")}
                  value={data.totals.sectionViews}
                />
                <EngagementMetric
                  label={t("engagementPanel.metrics.appLaunches")}
                  value={data.totals.appLaunches}
                />
                <EngagementMetric
                  label={t("engagementPanel.metrics.timeInApps")}
                  value={duration(data.totals.appSeconds)}
                />
                <EngagementMetric
                  label={t("engagementPanel.metrics.radioPlays")}
                  value={data.totals.radioPlays}
                />
                {showCasting && (
                  <EngagementMetric
                    label={t("engagementPanel.metrics.screenShares")}
                    value={data.totals.castStarts}
                    hint={t("engagementPanel.metrics.castingHint", {
                      duration: duration(data.totals.castSeconds),
                    })}
                  />
                )}
                <EngagementMetric
                  label={t("engagementPanel.metrics.inputSwitches")}
                  value={data.totals.inputSwitches}
                />
              </Stack>

              {!hasData && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  {t("engagementPanel.noData")}
                </Alert>
              )}

              <Divider sx={{ my: 3 }} />

              <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
                <EngagementBreakdown
                  title={t("engagementPanel.breakdowns.sections")}
                  entries={data.sections}
                  empty={t("engagementPanel.breakdowns.sectionsEmpty")}
                />
                <EngagementBreakdown
                  title={t("engagementPanel.breakdowns.stations")}
                  entries={data.stations}
                  empty={t("engagementPanel.breakdowns.stationsEmpty")}
                />
                <EngagementBreakdown
                  title={t("engagementPanel.breakdowns.languages")}
                  entries={data.languages}
                  empty={t("engagementPanel.breakdowns.languagesEmpty")}
                />
                <EngagementBreakdown
                  title={t("engagementPanel.breakdowns.info")}
                  entries={data.info}
                  empty={t("engagementPanel.breakdowns.infoEmpty")}
                />
              </Stack>
            </CardContent>
          </Card>

          <AppsCard summary={data} duration={duration} />
          <PromotionsCard summary={data} duration={duration} />

          <Typography variant="caption" color="text.secondary">
            {t("engagementPanel.retention", {
              period:
                Math.round(data.retentionDays / 365.25 || 0) >= 1
                  ? t("engagementPanel.retentionYears", {
                      years: fmt.number(data.retentionDays / 365.25, {
                        minimumFractionDigits: 1,
                        maximumFractionDigits: 1,
                      }),
                    })
                  : t("engagementPanel.retentionDays", {
                      days: fmt.number(data.retentionDays),
                    }),
              days: fmt.number(data.retentionDays),
            })}
          </Typography>
        </>
      )}
    </Stack>
  );
}

/**
 * Per-app attribution: how often each app is opened, and how long guests stay.
 *
 * A launch count alone cannot separate the app someone watched all evening from
 * the one they bounced straight back out of, which is exactly the distinction a
 * property needs when deciding which services to license. So the average
 * session sits next to the launch count, and the bar is scaled by TIME rather
 * than launches - the ranking by clicks is already above it.
 */
function AppsCard({
  summary,
  duration,
}: {
  summary: EngagementSummary;
  duration: (seconds: number | undefined | null) => string;
}) {
  const { t } = useTranslation("analytics");
  const fmt = useFormat();
  const apps = summary.apps.slice(0, 12);
  const maxSeconds = Math.max(1, ...apps.map((a) => a.seconds ?? 0));
  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="subtitle1" gutterBottom>
          {t("engagementPanel.apps.title")}
        </Typography>
        {apps.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t("engagementPanel.apps.empty")}
          </Typography>
        ) : (
          <Table size="small" data-testid="engagement-apps">
            <TableHead>
              <TableRow>
                <TableCell>{t("engagementPanel.apps.columns.app")}</TableCell>
                <TableCell align="right">
                  {t("engagementPanel.apps.columns.launches")}
                </TableCell>
                <TableCell align="right">
                  {t("engagementPanel.apps.columns.totalTime")}
                </TableCell>
                <TableCell align="right">
                  {t("engagementPanel.apps.columns.avgSession")}
                </TableCell>
                <TableCell sx={{ width: "28%" }}>
                  {t("engagementPanel.apps.columns.shareOfTime")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {apps.map((a) => (
                <TableRow key={a.id} hover>
                  <TableCell>{a.label}</TableCell>
                  <TableCell align="right">{fmt.number(a.count)}</TableCell>
                  <TableCell align="right">{duration(a.seconds)}</TableCell>
                  <TableCell align="right">{duration(a.avgSeconds)}</TableCell>
                  <TableCell>
                    <LinearProgress
                      variant="determinate"
                      value={((a.seconds ?? 0) / maxSeconds) * 100}
                      sx={{ height: 6, borderRadius: 3 }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * The content-ROI report: which promotions a property should keep running.
 *
 * "Engaged" is the honest conversion proxy a television can produce. We know a
 * promotion was on the glass and for how long; we cannot know whether a guest
 * scanned the QR, because finding out would mean identifying the guest. So the
 * report answers the question it can answer truthfully - how often the slot was
 * up long enough to act on - and names the threshold rather than dressing it up
 * as a conversion rate.
 */
function PromotionsCard({
  summary,
  duration,
}: {
  summary: EngagementSummary;
  duration: (seconds: number | undefined | null) => string;
}) {
  const { t } = useTranslation("analytics");
  const fmt = useFormat();
  const slots = summary.slots.slice(0, 12);
  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="subtitle1" gutterBottom>
          {t("engagementPanel.promotions.title")}
        </Typography>
        {slots.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t("engagementPanel.promotions.empty")}
          </Typography>
        ) : (
          <Table size="small" data-testid="engagement-slots">
            <TableHead>
              <TableRow>
                <TableCell>
                  {t("engagementPanel.promotions.columns.promotion")}
                </TableCell>
                <TableCell align="right">
                  {t("engagementPanel.promotions.columns.impressions")}
                </TableCell>
                <TableCell align="right">
                  {t("engagementPanel.promotions.columns.engaged")}
                </TableCell>
                <TableCell align="right">
                  {t("engagementPanel.promotions.columns.engagedRate")}
                </TableCell>
                <TableCell align="right">
                  {t("engagementPanel.promotions.columns.avgTimeOnScreen")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {slots.map((s) => (
                <TableRow key={s.id} hover>
                  <TableCell>
                    <Tooltip title={s.id}>
                      <span>{s.label}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">{fmt.number(s.count)}</TableCell>
                  <TableCell align="right">
                    {fmt.number(s.engagedViews)}
                  </TableCell>
                  <TableCell align="right">
                    {formatPercent(s.engagedRate, fmt)}
                  </TableCell>
                  <TableCell align="right">{duration(s.avgSeconds)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
